import { ICommonMobileSetting, ICommonSettings } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigDeeplinkSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingDeeplink?: ICommonMobileSetting;
}

export interface IConfigDeeplinkSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingDeeplinkId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigDeeplinkSettings extends React.PureComponent<
  IConfigDeeplinkSettingsProps,
  IConfigDeeplinkSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigDeeplinkSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingDeeplink: undefined
    };
  }

  componentDidMount() {
    this.loadDeeplinkSettings();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigDeeplinkSettingsProps & IWithSDProps) {
    if (prevProps.editingDeeplinkId && !this.props.editingDeeplinkId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingDeeplinkId !== this.props.editingDeeplinkId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadDeeplinkSettings();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadDeeplinkSettings();
    }
  }

  loadDeeplinkSettings = () => {
    if (!this.props.editingDeeplinkId) return;

    const deepLinkSetting = _.cloneDeep(this.props.commonSettings?.commonMobileSettings);
    const editingDeeplink = deepLinkSetting?.find((setting) => setting.id === this.props.editingDeeplinkId);
    if (editingDeeplink)
      this.setState({ editingDeeplink }, () => {
        this.formRef.current?.resetFields();
        this.formRef.current?.setFieldsValue(editingDeeplink);
      });
  };

  existingNameValidator = async (_rule, value) => {
    const deepLinkSettings = this.props.commonSettings?.commonMobileSettings;

    const isSettingAlreadyExist =
      deepLinkSettings?.some((x) => x.name === value && x.id !== this.props.editingDeeplinkId) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Deeplink Setting Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    const obj: ICommonMobileSetting = {
      id:
        this.props.editingDeeplinkId && this.props.editingDeeplinkId !== ""
          ? this.props.editingDeeplinkId
          : Guid.create().toString(),
      name: data.name,
      associatedDomain: data.associatedDomain?.trim(),
      AssociatedSubDomain: data.AssociatedSubDomain?.trim()
    };
    if (this.props.editingDeeplinkId) {
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "commonMobileSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "commonMobileSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteDeeplink = () => {
    if (!this.props.editingDeeplinkId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingDeeplinkId || "",
      configKey: "commonMobileSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Deeplink setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Deeplink setting"
        secondHeader="Deeplink setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingDeeplinkId ? this.deleteDeeplink : undefined}
            deleteTestId="deeplinkSettingDeleteButton"
            submitTestId="deeplinkSettingCreateButton"
            submitLabel={this.props.editingDeeplinkId ? "UPDATE" : "CREATE"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configDeeplinkForm"
          />
        }
      >
        <Form
          id="configDeeplinkForm"
          name="deepLinkSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("deeplinksettingspopup_deeplinksettings_description")}
          >
            <div>
              <ConfigField label="Name" required tooltipContentKey="deeplinksettingspopup_deeplinksettings_name">
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a Deeplink Setting Name." },
                    { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Deeplink Setting Name is invalid" },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="deeplinkSettingNameInput" autoFocus />
                </Form.Item>
              </ConfigField>
              <ConfigField
                label="Associated Domain"
                required
                tooltipContentKey="deeplinksettingspopup_deeplinksettings_associateddomain"
              >
                <Form.Item
                  name="associatedDomain"
                  rules={[{ required: true, message: "Please enter a Associated Domain." }]}
                >
                  <KuikaInput size="md" data-testid="deeplinkSettingAssociatedDomainInput" />
                </Form.Item>
              </ConfigField>
              <ConfigField
                label="Associated Sub Domain"
                tooltipContentKey="deeplinksettingspopup_deeplinksettings_associatedsubdomain"
              >
                <Form.Item name="AssociatedSubDomain">
                  <KuikaInput size="md" />
                </Form.Item>
              </ConfigField>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
